import { z } from 'zod';
import { Id, IsoDate } from './common';
/* ============================================================================
   RBAC (PLAN §3). Check order on every request:
     entitlement (plan allows it?) → permission (role has it?) → membership (resource scope)
   Permission keys use the CODE name "mission" for tasks (UI wording is separate).
   ========================================================================== */
export const PERMISSIONS = [
    // company
    'tenant.view',
    'tenant.branding.edit',
    'tenant.domain.manage',
    'tenant.billing.view',
    'tenant.billing.request',
    'tenant.export',
    'tenant.delete',
    'tenant.ownership.transfer',
    'tenant.audit.view',
    // people
    'user.view',
    'user.invite',
    'user.role.change',
    'user.deactivate',
    'user.remove',
    'role.manage',
    // workspaces & structure
    'workspace.view',
    'workspace.create',
    'workspace.edit',
    'workspace.delete',
    'workspace.member.manage',
    'project.view',
    'project.create',
    'project.edit',
    'project.archive',
    'project.delete',
    'project.member.manage',
    'status.manage',
    'field.manage',
    'view.manage',
    'client.manage',
    // missions
    'mission.view',
    'mission.create',
    'mission.edit',
    'mission.edit.any',
    'mission.assign',
    'mission.delete',
    'mission.bulk',
    'comment.create',
    'comment.edit.any',
    'comment.delete.any',
    'attachment.upload',
    'attachment.delete.any',
    // sticky boards
    'board.view',
    'board.edit',
    'board.manage',
];
/** Mission HQ permissions (platform users). */
export const PLATFORM_PERMISSIONS = [
    'hq.overview.view',
    'hq.tenant.view',
    'hq.tenant.manage',
    'hq.tenant.enter',
    'hq.tenant.delete',
    'hq.subscription.manage',
    'hq.entitlement.override',
    'hq.plan.manage',
    'hq.inquiry.manage',
    'hq.offer.manage',
    'hq.broadcast.send',
    'hq.release.publish',
    'hq.jobs.run',
    'hq.team.manage',
    'hq.audit.view',
];
const ALL = [...PERMISSIONS];
const without = (...p) => ALL.filter((x) => !p.includes(x));
/** Default permissions for system tenant roles (seeded per tenant). */
export const DEFAULT_ROLE_PERMISSIONS = {
    owner: ALL,
    admin: without('tenant.delete', 'tenant.ownership.transfer'),
    manager: [
        'tenant.view',
        'user.view',
        'workspace.view',
        'workspace.member.manage',
        'project.view',
        'project.create',
        'project.edit',
        'project.archive',
        'project.member.manage',
        'status.manage',
        'view.manage',
        'client.manage',
        'mission.view',
        'mission.create',
        'mission.edit',
        'mission.edit.any',
        'mission.assign',
        'mission.delete',
        'mission.bulk',
        'comment.create',
        'attachment.upload',
        'board.view',
        'board.edit',
        'board.manage',
    ],
    member: [
        'tenant.view',
        'user.view',
        'workspace.view',
        'project.view',
        'mission.view',
        'mission.create',
        'mission.edit',
        'mission.assign',
        'comment.create',
        'attachment.upload',
        'board.view',
        'board.edit',
    ],
    guest: ['project.view', 'mission.view', 'comment.create', 'board.view'],
};
export const DEFAULT_PLATFORM_PERMISSIONS = {
    owner: [...PLATFORM_PERMISSIONS],
    admin: PLATFORM_PERMISSIONS.filter((p) => p !== 'hq.team.manage' && p !== 'hq.tenant.delete'),
    support: ['hq.overview.view', 'hq.tenant.view', 'hq.tenant.enter', 'hq.inquiry.manage', 'hq.audit.view'],
    viewer: ['hq.overview.view', 'hq.tenant.view'],
};
export const RoleSchema = z.object({
    id: Id,
    key: z.string(),
    name: z.string(),
    isSystem: z.boolean(),
    permissions: z.array(z.enum(PERMISSIONS)),
    memberCount: z.number().int().nonnegative(),
    createdAt: IsoDate,
    updatedAt: IsoDate,
});
export const RoleCreateSchema = z.object({
    name: z.string().trim().min(2).max(40),
    copyFromRoleId: Id.optional(),
    permissions: z.array(z.enum(PERMISSIONS)).optional(),
});
export const RoleUpdateSchema = z.object({
    name: z.string().trim().min(2).max(40).optional(),
    permissions: z.array(z.enum(PERMISSIONS)).optional(),
});
/** Permission × role grid shown in Settings → Roles & permissions. */
export const PermissionMatrixSchema = z.object({
    permissions: z.array(z.object({ key: z.enum(PERMISSIONS), group: z.string(), label: z.string() })),
    roles: z.array(RoleSchema),
});
//# sourceMappingURL=rbac.js.map